import * as React from 'react';
import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  DetailsRow,
  IDetailsRowStyles,
  IDetailsListProps,
  CheckboxVisibility,
} from '@fluentui/react/lib/DetailsList';
import { TextField, Toggle } from 'office-ui-fabric-react';
import { IResumenColaboradoresComp } from '../../../system/panel/PanelSystem';
import { DateTime } from 'luxon';
import { routes } from '../../../common/constants';
import { useNavigate } from 'react-router-dom';
import { OrganizationChartNode } from '../../../system/organization_chart/OrganizationChartNode';
import { useTranslation } from 'react-i18next';

interface ICollaboratorsComparisonTable {
  collaboratorComp: IResumenColaboradoresComp[];
  from: DateTime;
  to: DateTime;
  nodes: OrganizationChartNode[];
  searchCollaboratorName: string;
  onSearchCollaboratorNameChange: (value: string) => void;
  withoutAssignedHoursOnly: boolean;
  onWithoutAssignedHoursOnlyChange: (value: boolean) => void;
}

interface ICollaboratorItem {
  id: string;
  name: string;
  col_id_sap: string;
  horas_totales: number;
  docs_totales: number;
  sector: string;
  cant_actividades: number;
  cant_doc_actividad: number;
  cant_doc_proyecto: number;
  cant_doc_servicio: number;
}

const controlStyles = {
  root: {
    display: 'inline-block',
    margin: '0 30px 20px 0',
    maxWidth: '300px',
  },
};

export const CollaboratorsComparisonTable = (props: ICollaboratorsComparisonTable) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const baseItems = React.useMemo<ICollaboratorItem[]>(() => {
    return props.collaboratorComp.map((col) => {
      const node = props.nodes.find((n) => n.isIdentifiedBy(col.colaborador.cargoColaborador?.carcol_norid || ''));
      if (!node) {
        console.error(
          `❌ Nodo no encontrado.\n` +
            `🔹 carcol_norid: ${col.colaborador.cargoColaborador?.carcol_norid}\n` +
            `🔹 col_id: ${col.colaborador.col_id}`,
        );
      }
      return {
        id: col.colaborador.col_id,
        name: `${col.colaborador.col_nombre} ${col.colaborador.col_apellido}`,
        col_id_sap: col.colaborador.col_id_sap,
        horas_totales: col.horas_totales,
        docs_totales: col.docs_totales,
        sector: node ? node.path() : t('⚠️ no encontrado'),
        cant_actividades: col.cant_actividades,
        cant_doc_actividad: col.cant_doc_actividad,
        cant_doc_proyecto: col.cant_doc_proyecto,
        cant_doc_servicio: col.cant_doc_servicio,
      };
    });
  }, [props.collaboratorComp, props.nodes, t]);

  const [itemsFiltered, setItemsFiltered] = React.useState<ICollaboratorItem[]>([]);
  const [sortConfig, setSortConfig] = React.useState<{ key: string; isDescending: boolean }>({ key: '', isDescending: false });

  const copyAndSort = <T,>(itemsToSort: T[], columnKey: string, isSortedDescending?: boolean): T[] => {
    const key = columnKey as keyof T;
    return [...itemsToSort].sort((a: T, b: T) => {
      if (a[key] === b[key]) return 0;
      return (isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1;
    });
  };

  const onColumnClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    const isDesc = sortConfig.key === column.key ? !sortConfig.isDescending : true;
    setSortConfig({ key: column.key, isDescending: isDesc });

    setItemsFiltered((currentItems) => copyAndSort(currentItems, column.fieldName!, isDesc));
  };

  const columns = React.useMemo<IColumn[]>(() => {
    const colDefinitions = [
      { key: 'name', name: t('Colaborador'), fieldName: 'name', minWidth: 250, maxWidth: 250 },
      { key: 'sap_id', name: t('SAP ID'), fieldName: 'col_id_sap', minWidth: 150, maxWidth: 150 },
      { key: 'sector', name: t('Sector'), fieldName: 'sector', minWidth: 600, maxWidth: 600 },
      { key: 'cant_actividades', name: t('Total de actividades'), fieldName: 'cant_actividades', minWidth: 140, maxWidth: 140 },
      { key: 'horas_totales', name: t('Horas totales'), fieldName: 'horas_totales', minWidth: 100, maxWidth: 100 },
      { key: 'cant_doc_actividad', name: t('Total de documentos por actividad'), fieldName: 'cant_doc_actividad', minWidth: 240, maxWidth: 240 },
      { key: 'cant_doc_proyecto', name: t('Total de documentos por proyecto'), fieldName: 'cant_doc_proyecto', minWidth: 240, maxWidth: 240 },
      { key: 'cant_doc_servicio', name: t('Total de documentos por servicio'), fieldName: 'cant_doc_servicio', minWidth: 240, maxWidth: 240 },
      { key: 'docs_totales', name: t('Documentos totales'), fieldName: 'docs_totales', minWidth: 160, maxWidth: 160 },
    ];

    return colDefinitions.map((col) => ({
      ...col,
      onColumnClick: onColumnClick,
      isSorted: sortConfig.key === col.key,
      isSortedDescending: sortConfig.key === col.key ? sortConfig.isDescending : undefined,
    }));
  }, [sortConfig, t]);

  React.useEffect(() => {
    let filtered = baseItems.filter((item) => {
      const matchesText = !props.searchCollaboratorName || item.name.toLowerCase().includes(props.searchCollaboratorName.toLowerCase());

      const matchesHours = !props.withoutAssignedHoursOnly || item.horas_totales === 0;

      return matchesText && matchesHours;
    });

    if (sortConfig.key) {
      const activeColumn = columns.find((c) => c.key === sortConfig.key);
      if (activeColumn?.fieldName) {
        filtered = copyAndSort(filtered, activeColumn.fieldName, sortConfig.isDescending);
      }
    }

    setItemsFiltered(filtered);
  }, [baseItems, props.searchCollaboratorName, props.withoutAssignedHoursOnly, sortConfig.key, sortConfig.isDescending]);

  const onChangeText = (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, text: string | undefined): void => {
    props.onSearchCollaboratorNameChange(text || '');
  };

  const onChangeHsFilter = (ev: React.MouseEvent<HTMLElement>, checked: boolean | undefined): void => {
    props.onWithoutAssignedHoursOnlyChange(!!checked);
  };

  const _onRenderRow: IDetailsListProps['onRenderRow'] = (propsRow) => {
    if (!propsRow) return null;

    const customStyles: Partial<IDetailsRowStyles> = {
      cell: { cursor: 'pointer' },
    };

    if (propsRow.item.horas_totales === 0) {
      customStyles.root = { backgroundColor: '#ffbbbb' };
    }

    const handleRowClick = () => {
      const fromStr = props.from.toFormat('yyyy-LL-dd');
      const toStr = props.to.toFormat('yyyy-LL-dd');
      navigate(`${routes.collaborator}/${propsRow.item.id}?showPanel=1&from=${fromStr}&to=${toStr}`);
    };

    return (
      <div onClick={handleRowClick}>
        <DetailsRow {...propsRow} styles={customStyles} />
      </div>
    );
  };

  const _getKey = (item: ICollaboratorItem): string => item.id;

  return (
    <div data-is-scrollable="true" style={{ width: '100%', overflowX: 'auto' }}>
      <TextField label={t('Filter by name:')} value={props.searchCollaboratorName} onChange={onChangeText} styles={controlStyles} />
      <Toggle
        label={t('Filtrar por Horas')}
        checked={props.withoutAssignedHoursOnly}
        onChange={onChangeHsFilter}
        onText={t('Solo usuarios sin Hs')}
        offText={t('Todos')}
        styles={controlStyles}
      />
      <DetailsList
        items={itemsFiltered}
        columns={columns}
        getKey={_getKey}
        setKey="set"
        layoutMode={DetailsListLayoutMode.justified}
        checkboxVisibility={CheckboxVisibility.hidden}
        onRenderRow={_onRenderRow}
        onShouldVirtualize={() => false}
      />
    </div>
  );
};
